/**
 * Export orchestration hook
 * Story 464: Export full project data as a downloadable zip of markdown files
 *
 * Manages the full export flow: fetch → markdown → zip → download.
 * JSZip is lazy-loaded on first export to avoid bloating the initial bundle.
 */

import { useState, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { generateExportFiles, type ProjectExportData } from '../utils/exportMarkdown';

type ExportState = 'idle' | 'exporting' | 'ready' | 'error';

/** Auto-dismiss timeout for the download butter bar */
const DOWNLOAD_DISMISS_MS = 30000;

/** Auto-dismiss timeout for error butter bar */
const ERROR_DISMISS_MS = 8000;

export interface UseProjectExportReturn {
  startExport: (slug: string) => void;
  isExporting: boolean;
  exportReady: boolean;
  exportError: string | null;
  download: () => void;
  dismiss: () => void;
  autoDismissMs: number;
}

export function useProjectExport(): UseProjectExportReturn {
  const [state, setState] = useState<ExportState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const filenameRef = useRef<string>('export.zip');
  const inFlightRef = useRef(false);

  const startExport = useCallback(async (slug: string) => {
    // Prevent concurrent exports
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setState('exporting');
    setErrorMessage(null);
    blobRef.current = null;

    try {
      // 1. Fetch export data
      const data: ProjectExportData = await api.fetch(`/api/v1/projects/${encodeURIComponent(slug)}/export`);

      // 2. Generate markdown files
      const files = generateExportFiles(data);

      // 3. Lazy-load JSZip
      const JSZip = (await import('jszip')).default;

      // 4. Create zip
      const zip = new JSZip();
      const folderName = slug.replace(/\//g, '-');
      const folder = zip.folder(folderName)!;

      for (const [path, content] of files) {
        folder.file(path, content);
      }

      blobRef.current = await zip.generateAsync({ type: 'blob' });
      filenameRef.current = `${folderName}.zip`;

      setState('ready');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      setErrorMessage(message);
      setState('error');
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const download = useCallback(() => {
    if (!blobRef.current) return;

    const url = URL.createObjectURL(blobRef.current);
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameRef.current;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Reset state after download
    setState('idle');
    blobRef.current = null;
  }, []);

  const dismiss = useCallback(() => {
    setState('idle');
    setErrorMessage(null);
    blobRef.current = null;
  }, []);

  return {
    startExport,
    isExporting: state === 'exporting',
    exportReady: state === 'ready',
    exportError: state === 'error' ? errorMessage : null,
    download,
    dismiss,
    autoDismissMs: state === 'error' ? ERROR_DISMISS_MS : DOWNLOAD_DISMISS_MS,
  };
}
